/**
 * Perimeter text 描画（happa v1.1、spec §5.4.3）。
 *
 * `drawTextAlongPath` は `TextPathData`（warped silhouette を offset した
 * テキスト経路）に沿って、tracking + 和欧混植を適用しつつ 1 文字ずつ
 * `ctx.fillText` で描画する副作用関数。
 *
 * 2026-07-10: detonate 中の文字被り根本修正。配置計算（どこに・どの角度で
 * 置くか）を pure モジュール `core/textLayout.ts#computeGlyphPlacements` に
 * 移管した。同モジュールは弦ベースの衝突回避送り + 弦接線でグリフを配置し、
 * warp された凹カーブでも隣接グリフが重ならないことを保証する。
 * 本ファイルの責務は「幅測定（ctx 依存）と fillText」のみ。
 *
 * 描画単位:
 * - `chars = [...text]`（サロゲートペアを 1 要素として安全に分割）
 * - 1 文字ごとに `isCJK` で Latin / Japanese フォントを切り替え、
 *   `ctx.measureText` で実幅を取得
 * - リピート回数・ジャスティファイ・中央 1 周クリップの挙動は
 *   `computeGlyphPlacements` 側で旧実装と同型を維持
 *
 * 副作用は `ctx` の描画のみ。`ctx.save()/restore()` で fillStyle / font /
 * textBaseline / transform を常にカプセル化し、呼び出し側の state を
 * 汚染しない。空テキストや退化 pathData は early return で描画なし。
 *
 * 設計参照:
 * - `docs/happa-designtool-v1.1-spec.md` §5.4.3 L557–647
 * - `src/core/textLayout.ts`（配置アルゴリズムの詳細 JSDoc）
 *
 * 禁止事項（CLAUDE.md / spec §2.1）:
 * - shadow / gradient / filter の装飾的使用
 * - 追加のアニメーションやエフェクト
 * - ctx への state 流出（`save`/`restore` を省略しない）
 */

import { computeGlyphPlacements, type PlacementOptions } from '../core/textLayout';
import type { TextPathData } from '../core/types';
import { buildFontSpec, isCJK } from '../core/typography';

/**
 * `drawTextAlongPath` の描画スタイル（spec §5.4.3 の `style` 引数）。
 *
 * tracking は spec §A.2 の UI scale（tracking: -10..100）をそのまま受け取り、
 * `fontSize` に対するパーセンテージとして内部で `fontSize × value / 100` に
 * 換算する。
 */
export interface DrawTextStyle {
  /** フォントサイズ（px）。`Layout.fontSize` 由来を想定。 */
  fontSize: number;
  /**
   * 字間（%、`fontSize` に対する加算幅）。
   * spec §A.2: UI range `-10..100`、default `10`。
   * 負値で詰め、100 で文字 1 個分の空白に相当する。
   */
  tracking: number;
  /** Latin（欧文）フォントの CSS `font-family` 文字列。 */
  latinFontFamily: string;
  /** Japanese（和文）フォントの CSS `font-family` 文字列。 */
  jpFontFamily: string;
  /** テキスト色（HEX）。`ctx.fillStyle` にそのまま代入する。 */
  color: string;
}

/**
 * `TextPathData` に沿ってテキストを描画する（spec §5.4.3）。
 *
 * 呼び出し前提:
 * - `ctx` のサイズは outer canvas 寸法に同期済み
 * - `pathData` は `buildTextPath` の返り値（`totalLength > 0` が望ましい）
 * - 呼び出し側の fillStyle / font は汚染されない（本関数で save/restore 済）
 *
 * 早期 return 条件:
 * - `text` が空（`text === ''`）
 * - `chars.length === 0`
 * - `pathData.totalLength === 0` 等の退化（`computeGlyphPlacements` が空を返す）
 *
 * @param ctx 描画先の 2D コンテキスト。
 * @param pathData 経路データ（`buildTextPath` の返り値）。
 * @param text 描画する文字列（空文字可）。
 * @param style フォント / 字間 / 色の設定。
 * @param layoutOptions 配置オプション（reps ヒステリシス等）。呼び出し側が
 *        前フレームの返り値を `preferredReps` として渡すと、pulse の周長
 *        振動でテキスト 1 周分が出現/消滅を繰り返すのを防げる。
 * @returns 採用したリピート数（中央 1 周ブランチ・early return では 0）。
 */
export function drawTextAlongPath(
  ctx: CanvasRenderingContext2D,
  pathData: TextPathData,
  text: string,
  style: DrawTextStyle,
  layoutOptions: PlacementOptions = {},
): number {
  if (text === '') return 0;
  const chars = [...text];
  if (chars.length === 0) return 0;

  const { fontSize } = style;
  const trackingPx = fontSize * (style.tracking / 100);

  const latinSpec = buildFontSpec(style.latinFontFamily, fontSize);
  const jpSpec = buildFontSpec(style.jpFontFamily, fontSize);

  // 1 文字ずつ font を切り替えて幅測定。ctx.font の切替は measureText の結果に
  // 直接効くので、測定と描画で同じ spec を使うことが幅ズレ回避の必須条件。
  const widths: number[] = [];
  const isJpArr: boolean[] = [];
  ctx.save();
  for (const c of chars) {
    const jp = isCJK(c);
    isJpArr.push(jp);
    ctx.font = jp ? jpSpec : latinSpec;
    widths.push(ctx.measureText(c).width);
  }
  ctx.restore();

  const { placements, reps } = computeGlyphPlacements(
    pathData,
    widths,
    trackingPx,
    fontSize,
    layoutOptions,
  );

  for (const p of placements) {
    const c = chars[p.charIndex];
    const jp = isJpArr[p.charIndex];
    const w = widths[p.charIndex];
    if (c === undefined || jp === undefined || w === undefined) continue;
    ctx.save();
    ctx.font = jp ? jpSpec : latinSpec;
    ctx.fillStyle = style.color;
    ctx.textBaseline = 'alphabetic';
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    // 第 2 引数 `-w/2` は中央揃え、第 3 引数 `-fontSize × 0.25` は「文字の
    // ベースラインをパスより少し内側（文字高さの ¼ 分）に落とす」補正
    // （spec §5.4.3 L611、旧実装から不変）。
    ctx.fillText(c, -w / 2, -fontSize * 0.25);
    ctx.restore();
  }
  return reps;
}
